import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  deleteAircraftDocument,
  downloadAircraftDocument,
  getAircraft,
  getAircraftDocumentFile,
  getAircraftDocuments,
  updateAircraftInsurance,
  updateAircraftRegistration,
  updateAircraftSlg,
  uploadAircraftDocument,
} from '../../api';
import {
  Aircraft,
  AircraftDocument,
  AircraftDocumentType,
  AircraftSlg,
} from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import { createUuid } from '../../utils/createUuid';
import { emptySlg } from '../../utils/emptySlg';
import {
  blobWithMime,
  previewKindFromName,
  type PreviewKind,
} from '../../utils/filePreview';
import DocumentPreviewModal from '../../components/DocumentPreviewModal/DocumentPreviewModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import './AircraftPage.css';

const getToday = () => new Date().toISOString().split('T')[0];

const DOC_TYPE_OPTIONS: { value: AircraftDocumentType; label: string }[] = [
  { value: 'slg', label: 'СЛГ' },
  { value: 'insurance', label: 'Страховка' },
  { value: 'registration', label: 'Свидетельство о регистрации' },
  { value: 'ps', label: 'Свидетельство о ПС' },
  { value: 'other', label: 'Иное' },
];

const REPLACEABLE_DOC_TYPES: AircraftDocumentType[] = [
  'slg',
  'insurance',
  'registration',
];
const STACKED_DOC_TYPES = new Set<AircraftDocumentType>(['ps', 'other']);
const TYPE_ORDER: Record<string, number> = {
  slg: 0,
  insurance: 1,
  registration: 2,
  ps: 3,
  other: 4,
};

const docTypeLabel = (docType: string) =>
  DOC_TYPE_OPTIONS.find((item) => item.value === docType)?.label || docType;

const checkStatusKind = (item: AircraftSlg) => {
  if (item.overdue) return 'overdue';
  if (!item.last_date) return 'missing';
  if (item.days_left != null && item.days_left <= 30) return 'soon';
  return 'ok';
};

const checkStatusText = (item: AircraftSlg, dateFormat: 'dot' | 'long') => {
  if (!item.last_date) return 'Ещё не указано';
  if (!item.next_date) {
    return `Выдано: ${DateFormat(item.last_date, dateFormat)}`;
  }
  const next = DateFormat(item.next_date, dateFormat);
  if (item.overdue) return `Просрочено с ${next}`;
  if (item.days_left != null && item.days_left <= 30) {
    return `До ${next} (осталось ${item.days_left} дн.)`;
  }
  return `Следующий срок: ${next}`;
};

type DocGroup = {
  key: string;
  doc_type: string;
  created_at: string | null;
  files: AircraftDocument[];
};

function groupDocuments(docs: AircraftDocument[]): DocGroup[] {
  const stacked = new Map<string, AircraftDocument[]>();
  const map = new Map<string, DocGroup>();
  for (const doc of docs) {
    if (STACKED_DOC_TYPES.has(doc.doc_type as AircraftDocumentType)) {
      const list = stacked.get(doc.doc_type) || [];
      list.push(doc);
      stacked.set(doc.doc_type, list);
      continue;
    }
    const key = doc.group_id || `solo-${doc.id}`;
    const existing = map.get(key);
    if (existing) {
      existing.files.push(doc);
      if (
        doc.created_at &&
        (!existing.created_at || doc.created_at > existing.created_at)
      ) {
        existing.created_at = doc.created_at;
      }
    } else {
      map.set(key, {
        key,
        doc_type: doc.doc_type,
        created_at: doc.created_at,
        files: [doc],
      });
    }
  }
  const groups = Array.from(map.values());
  Array.from(stacked.entries()).forEach(([docType, files]) => {
    if (files.length === 0) return;
    groups.push({
      key: `${docType}-all`,
      doc_type: docType,
      created_at: files[0]?.created_at ?? null,
      files,
    });
  });
  groups.sort(
    (a, b) => (TYPE_ORDER[a.doc_type] ?? 9) - (TYPE_ORDER[b.doc_type] ?? 9)
  );
  return groups;
}

type CheckCardProps = {
  title: string;
  hint: string;
  item: AircraftSlg;
  draft: string;
  saving: boolean;
  canEdit: boolean;
  dateFormat: 'dot' | 'long';
  onDraftChange: (value: string) => void;
  onSave: () => void;
};

const CheckCard = ({
  title,
  hint,
  item,
  draft,
  saving,
  canEdit,
  dateFormat,
  onDraftChange,
  onSave,
}: CheckCardProps) => {
  const kind = checkStatusKind(item);
  return (
    <article className={`aircraft-card aircraft-check-card accent-${kind}`}>
      <div className="aircraft-section-head">
        <h3>{title}</h3>
        <p className="aircraft-section-hint">{hint}</p>
      </div>
      <p
        className={`aircraft-slg-status${
          kind === 'overdue' ? ' is-overdue' : ''
        }`}
      >
        {checkStatusText(item, dateFormat)}
      </p>
      {canEdit ? (
        <div className="aircraft-slg-actions">
          <input
            type="date"
            value={draft}
            max={getToday()}
            onChange={(e) => onDraftChange(e.target.value)}
            aria-label={`Дата выдачи: ${title}`}
          />
          <button
            type="button"
            className="btn-aircraft-save"
            disabled={saving}
            onClick={onSave}
          >
            {saving ? '…' : 'Сохранить'}
          </button>
        </div>
      ) : null}
    </article>
  );
};

const AircraftDocumentsPage = () => {
  const { canMaintain, user } = useAuth();
  const { aircraftId } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const dateFormat = user?.date_format === 'long' ? 'long' : 'dot';
  const [aircraft, setAircraft] = useState<Aircraft | null>(null);
  const [ready, setReady] = useState(false);
  const [slgDraft, setSlgDraft] = useState('');
  const [insuranceDraft, setInsuranceDraft] = useState('');
  const [registrationDraft, setRegistrationDraft] = useState('');
  const [savingSlg, setSavingSlg] = useState(false);
  const [savingInsurance, setSavingInsurance] = useState(false);
  const [savingRegistration, setSavingRegistration] = useState(false);
  const [documents, setDocuments] = useState<AircraftDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState<AircraftDocumentType>('slg');
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [previewFiles, setPreviewFiles] = useState<AircraftDocument[]>([]);
  const [previewDoc, setPreviewDoc] = useState<AircraftDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewKind>('other');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequest = useRef(0);

  const groups = useMemo(() => groupDocuments(documents), [documents]);

  const applyAircraft = useCallback((data: Aircraft) => {
    setAircraft(data);
    setSlgDraft(data.slg?.last_date || data.slg_date || '');
    setInsuranceDraft(data.insurance?.last_date || data.insurance_date || '');
    setRegistrationDraft(
      data.registration?.last_date || data.registration_date || ''
    );
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      const res = await getAircraftDocuments();
      setDocuments(res.data || []);
    } catch {
      setDocuments([]);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await getAircraft();
      applyAircraft(res.data);
      await loadDocuments();
    } catch {
      setAircraft(null);
      setDocuments([]);
    } finally {
      setReady(true);
    }
  }, [applyAircraft, loadDocuments]);

  useEffect(() => {
    setReady(false);
    void load();
  }, [aircraftId, load]);

  const handleSaveSlg = async () => {
    if (!canMaintain) return;
    if (!slgDraft) {
      showError('Укажите дату выдачи СЛГ');
      return;
    }
    setSavingSlg(true);
    try {
      const res = await updateAircraftSlg({ slg_date: slgDraft });
      if (aircraft) {
        applyAircraft({
          ...aircraft,
          slg_date: res.data.slg?.last_date || slgDraft,
          slg: res.data.slg || emptySlg(),
        });
      }
      showSuccess('Дата СЛГ сохранена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка сохранения СЛГ'));
    }
    setSavingSlg(false);
  };

  const handleSaveInsurance = async () => {
    if (!canMaintain) return;
    if (!insuranceDraft) {
      showError('Укажите дату страховки');
      return;
    }
    setSavingInsurance(true);
    try {
      const res = await updateAircraftInsurance({
        insurance_date: insuranceDraft,
      });
      if (aircraft) {
        applyAircraft({
          ...aircraft,
          insurance_date: res.data.insurance?.last_date || insuranceDraft,
          insurance: res.data.insurance || emptySlg(),
        });
      }
      showSuccess('Дата страховки сохранена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка сохранения страховки'));
    }
    setSavingInsurance(false);
  };

  const handleSaveRegistration = async () => {
    if (!canMaintain) return;
    if (!registrationDraft) {
      showError('Укажите дату свидетельства о регистрации');
      return;
    }
    setSavingRegistration(true);
    try {
      const res = await updateAircraftRegistration({
        registration_date: registrationDraft,
      });
      if (aircraft) {
        applyAircraft({
          ...aircraft,
          registration_date:
            res.data.registration?.last_date || registrationDraft,
          registration: res.data.registration || emptySlg(),
        });
      }
      showSuccess('Дата свидетельства о регистрации сохранена');
    } catch (error) {
      showError(
        getApiErrorMessage(
          error,
          'Ошибка сохранения свидетельства о регистрации'
        )
      );
    }
    setSavingRegistration(false);
  };

  const uploadFiles = async (files: File[], type: AircraftDocumentType) => {
    setUploading(true);
    const groupId = createUuid();
    let okCount = 0;
    try {
      for (const file of files) {
        await uploadAircraftDocument(file, type, groupId);
        okCount += 1;
      }
      await loadDocuments();
      showSuccess(
        okCount === 1 ? 'Документ загружен' : `Загружено файлов: ${okCount}`
      );
    } catch (error) {
      if (okCount > 0) {
        await loadDocuments();
      }
      showError(
        getApiErrorMessage(
          error,
          okCount > 0
            ? `Загружено ${okCount}, остальные не удалось`
            : 'Не удалось загрузить документ'
        )
      );
    }
    setUploading(false);
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0 || !canMaintain) return;
    const replacing =
      REPLACEABLE_DOC_TYPES.includes(docType) &&
      documents.some((item) => item.doc_type === docType);
    if (replacing) {
      setPendingFiles(files);
      return;
    }
    void uploadFiles(files, docType);
  };

  const closePreview = () => {
    previewRequest.current += 1;
    setPreviewDoc(null);
    setPreviewFiles([]);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewKind('other');
    setPreviewUrl(null);
  };

  const handlePreview = async (
    doc: AircraftDocument,
    files: AircraftDocument[] = [doc]
  ) => {
    setPreviewFiles(files);
    const requestId = previewRequest.current + 1;
    previewRequest.current = requestId;
    const name = doc.original_name || doc.filename;
    const kind = previewKindFromName(name);
    setPreviewUrl(null);
    setPreviewDoc(doc);
    setPreviewKind(kind);
    setPreviewError(null);
    if (kind === 'other') {
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await getAircraftDocumentFile(doc.id);
      if (previewRequest.current !== requestId) return;
      const blob = blobWithMime(res.data, name);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      if (previewRequest.current !== requestId) return;
      setPreviewError(getApiErrorMessage(error, 'Не удалось открыть документ'));
    }
    if (previewRequest.current === requestId) {
      setPreviewLoading(false);
    }
  };

  const previewIndex = previewDoc
    ? previewFiles.findIndex((item) => item.id === previewDoc.id)
    : -1;

  const goPreview = (index: number) => {
    const doc = previewFiles[index];
    if (!doc) return;
    void handlePreview(doc, previewFiles);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleDownload = async (doc: AircraftDocument) => {
    try {
      await downloadAircraftDocument(
        doc.id,
        doc.original_name || doc.filename
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось скачать документ'));
    }
  };

  const handleDeleteDoc = async (doc: AircraftDocument) => {
    if (!canMaintain) return;
    if (!window.confirm(`Удалить «${doc.original_name}»?`)) return;
    try {
      await deleteAircraftDocument(doc.id);
      setDocuments((prev) => prev.filter((item) => item.id !== doc.id));
      if (previewDoc?.id === doc.id) {
        closePreview();
      }
      showSuccess('Документ удалён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось удалить документ'));
    }
  };

  const slg = aircraft?.slg || emptySlg();
  const insurance = aircraft?.insurance || emptySlg();
  const registration = aircraft?.registration || emptySlg();

  if (ready && !aircraft) {
    return <Navigate to="/aircraft" replace />;
  }

  return (
    <div className="aircraft-page">
      {feedbackNode}
      <div className="aircraft-header aircraft-subpage-header">
        <Link to="/aircraft" className="aircraft-back-link">
          ← Самолёт
        </Link>
        <h2>Документы</h2>
        <p className="aircraft-subpage-hint">
          Даты СЛГ, страховки и свидетельства о регистрации, плюс файлы
        </p>
      </div>

      {aircraft ? (
        <>
          <div className="aircraft-meta-grid">
            <CheckCard
              title="СЛГ"
              hint="Лётная годность — раз в 2 года"
              item={slg}
              draft={slgDraft}
              saving={savingSlg}
              canEdit={canMaintain}
              dateFormat={dateFormat}
              onDraftChange={setSlgDraft}
              onSave={() => void handleSaveSlg()}
            />
            <CheckCard
              title="Страховка"
              hint="Выдаётся на год — контрольная дата для уведомлений"
              item={insurance}
              draft={insuranceDraft}
              saving={savingInsurance}
              canEdit={canMaintain}
              dateFormat={dateFormat}
              onDraftChange={setInsuranceDraft}
              onSave={() => void handleSaveInsurance()}
            />
            <CheckCard
              title="Свидетельство о регистрации"
              hint="Дата выдачи — без срока действия"
              item={registration}
              draft={registrationDraft}
              saving={savingRegistration}
              canEdit={canMaintain}
              dateFormat={dateFormat}
              onDraftChange={setRegistrationDraft}
              onSave={() => void handleSaveRegistration()}
            />
          </div>

          <div className="aircraft-card aircraft-docs-card">
            <div className="aircraft-section-head">
              <h3>Файлы</h3>
              <p className="aircraft-section-hint">
                PDF, фото или Word — до 10 МБ. Новый файл СЛГ, страховки или
                свидетельства о регистрации заменяет предыдущий. Свидетельства о
                ПС — по числу собственников; иное копится списком.
              </p>
            </div>
            {canMaintain ? (
              <div className="aircraft-docs-upload">
                <label className="aircraft-doc-type">
                  Тип
                  <select
                    value={docType}
                    onChange={(e) =>
                      setDocType(e.target.value as AircraftDocumentType)
                    }
                  >
                    {DOC_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="btn-aircraft-upload">
                  {uploading ? 'Загрузка...' : 'Прикрепить'}
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx"
                    multiple
                    disabled={uploading}
                    onChange={handleUpload}
                    hidden
                  />
                </label>
              </div>
            ) : null}
            {groups.length === 0 ? (
              <p className="aircraft-docs-empty">Документов пока нет</p>
            ) : (
              <ul className="aircraft-docs-list">
                {groups.map((group) => (
                  <li key={group.key} className="aircraft-doc-group">
                    <div className="aircraft-doc-meta">
                      <strong>{docTypeLabel(group.doc_type)}</strong>
                      {group.created_at ? (
                        <span>{DateFormat(group.created_at, dateFormat)}</span>
                      ) : null}
                    </div>
                    <ul className="aircraft-doc-files">
                      {group.files.map((doc) => (
                        <li key={doc.id} className="aircraft-doc-file">
                          <button
                            type="button"
                            className="aircraft-doc-file-open"
                            onClick={() => void handlePreview(doc, group.files)}
                          >
                            <span className="aircraft-doc-file-name">
                              {doc.original_name}
                            </span>
                          </button>
                          <div className="aircraft-doc-actions">
                            {canMaintain ? (
                              <button
                                type="button"
                                className="btn-aircraft-doc danger"
                                onClick={() => void handleDeleteDoc(doc)}
                              >
                                Удалить
                              </button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}

      {previewDoc ? (
        <DocumentPreviewModal
          title={previewDoc.original_name || previewDoc.filename}
          objectUrl={previewUrl}
          kind={previewKind}
          loading={previewLoading}
          error={previewError}
          fileIndex={Math.max(0, previewIndex)}
          fileCount={previewFiles.length}
          onPrev={() => goPreview(previewIndex - 1)}
          onNext={() => goPreview(previewIndex + 1)}
          onDownload={() => void handleDownload(previewDoc)}
          onClose={closePreview}
        />
      ) : null}

      {pendingFiles ? (
        <ConfirmModal
          title="Заменить документ?"
          message={`Новый файл «${docTypeLabel(docType)}» заменит предыдущий. Старые файлы этого типа будут удалены.`}
          confirmLabel="Заменить"
          cancelLabel="Отмена"
          danger
          submitting={uploading}
          onConfirm={() => {
            const files = pendingFiles;
            setPendingFiles(null);
            void uploadFiles(files, docType);
          }}
          onCancel={() => setPendingFiles(null)}
        />
      ) : null}
    </div>
  );
};

export default AircraftDocumentsPage;
